import {
  CaretRightFilled,
  DeleteOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import {
  Button,
  Collapse,
  Divider,
  InputRef,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { Input } from "antd/lib";
import { useAuthFetch } from "components/Login/hook";
import React, { useCallback, useRef, useState } from "react";
import { parseArgsStringToArgv } from "string-argv";
import styled from "styled-components";

import { CodeBlock } from "../../../Integrations/components/CodeBlock";

const SquareInput = styled(Input)`
  .ant-input-outlined {
    border-radius: 0px;
  }
`;

const MAX_HISTORY = 10;

const defaultMessage = "You have no output yet. Please execute a command.";

export const ShellPage: React.FC = () => {
  const inputRef = useRef<InputRef>(null);
  const [command, setCommand] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<{ command: string; result: string }[]>(
    []
  );

  const handleRecall = useCallback(
    (command: string) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();
      setCommand(command);
      inputRef.current?.focus();
    },
    []
  );

  const handleSaveResult = useCallback(
    (result: string) => {
      setHistory((prev) =>
        [{ command, result }, ...prev].slice(0, MAX_HISTORY)
      );
      setResult(result);
    },
    [command]
  );

  const authFetch = useAuthFetch((error) => {
    if (error) {
      handleSaveResult(error);
    }
  }, setLoading);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCommand(e.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    const argv = parseArgsStringToArgv(command);
    const response = await authFetch("command", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        argv,
        scriptName: "p0",
      }),
    });

    if (!response?.ok) {
      return;
    }

    const text = await response.text();
    const data = JSON.parse(text);
    if (data && "ok" in data && data.ok) {
      handleSaveResult(processResult(argv, data));
    }
  }, [authFetch, handleSaveResult, command]);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !loading) {
        handleSubmit();
      }
      if (e.key === "Escape") {
        setCommand("");
      }
    },
    [loading, handleSubmit]
  );

  const handleClear = useCallback(() => {
    setResult("");
  }, []);

  return (
    <div style={{ maxWidth: 720 }}>
      <Typography.Title level={2}>Cloud Shell</Typography.Title>
      <Typography.Paragraph>
        Try out real P0 CLI commands right here in your browser. Want to explore
        everything it can do?{" "}
        <a
          href="https://github.com/p0-security/p0cli/blob/main/README.md"
          target="_blank"
          rel="noopener noreferrer"
        >
          Check out our P0 CLI docs
        </a>
        .
      </Typography.Paragraph>
      <div>
        <Space.Compact style={{ width: "100%" }}>
          <SquareInput
            ref={inputRef}
            addonBefore="p0"
            placeholder="request ..."
            value={command}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            allowClear
          />
          <Button type="primary" onClick={handleSubmit} disabled={loading}>
            <CaretRightFilled />
            Execute
          </Button>
        </Space.Compact>
      </div>
      <Collapse
        style={{ marginTop: "1em" }}
        size="small"
        items={[
          {
            key: "1",
            label: "Command History",
            children: history.length ? (
              <div>
                <Typography.Paragraph
                  style={{
                    fontStyle: "italic",
                    color: "#888",
                    margin: "0.5em 0",
                  }}
                >
                  Showing your {Math.min(history.length, MAX_HISTORY)} most
                  recent requests:
                </Typography.Paragraph>
                <Collapse
                  size="small"
                  items={history.map((h, index) => ({
                    key: index,
                    label: (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "1em",
                        }}
                      >
                        <Typography.Paragraph
                          style={{
                            fontFamily: h.command ? "monospace" : "inherit",
                            fontSize: "0.9em",
                            fontStyle: h.command ? "normal" : "italic",
                            color: h.command ? "inherit" : "#888",
                            paddingBottom: 0,
                            marginBottom: 0,
                          }}
                        >
                          {h.command || "No arguments provided"}
                        </Typography.Paragraph>
                        <Tooltip title="Load this command without running it.">
                          <Button
                            size="small"
                            onClick={handleRecall(h.command)}
                          >
                            <RedoOutlined />
                            Recall
                          </Button>
                        </Tooltip>
                      </div>
                    ),
                    children: <CodeBlock>{h.result}</CodeBlock>,
                  }))}
                />
              </div>
            ) : (
              <Typography.Paragraph
                style={{
                  fontStyle: "italic",
                  color: "#888",
                  margin: "0.5em 0",
                }}
              >
                No history yet. Please execute a command.
              </Typography.Paragraph>
            ),
          },
        ]}
      />
      <Divider />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1em",
          justifyContent: "space-between",
          marginBottom: "0.5em",
        }}
      >
        <Typography.Title level={4}>
          Output{" "}
          {loading && (
            <Spin
              style={{
                marginLeft: "4px",
                marginBottom: "4px",
              }}
            />
          )}
        </Typography.Title>

        {!!result && (
          <Tooltip title="Clear all output.">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </Button>
          </Tooltip>
        )}
      </div>

      <CodeBlock>
        {loading
          ? "We are processing your request. Please wait..."
          : result || defaultMessage}
      </CodeBlock>
    </div>
  );
};

// TODO: The actual p0 CLI has support for more commands and does some post
// processing of the results we get back from the `/commands` endpoint.
// This is a temporary solution until we either implement the same logic in
// the backend API or share the logic between the frontend and CLI.
const processResult = (argv: string[], result: object): string => {
  const [command] = argv;
  if (command === "ls") {
    if (argv.includes("--json")) return JSON.stringify(result, null, 2);
    if (
      "items" in result &&
      typeof result.items === "object" &&
      Array.isArray(result.items)
    ) {
      const hasMore = "isTruncated" in result && result.isTruncated;
      const header = hasMore
        ? `Showing the first ${result.items.length} items:\n`
        : `Showing all items:\n`;
      const data = result.items
        .map((i: { value: string }) => `  ${i.value}`)
        .join("\n");
      return `${header}${data}`;
    }
  }

  if (command === "request" || command === "allow" || command === "grant") {
    if ("message" in result && typeof result.message === "string") {
      return result.message;
    }
  }

  return JSON.stringify(result, null, 2);
};
